import { withCreate } from "./activityCreate";

/**
 * Issue #460 — the Coach Portal's top ribbon.
 *
 * Martin: *"Add shortcuts for the following; 'Schedule training Session',
 * 'Provide feedback' and 'Assign coaching guide' on top ribbon with the
 * current shortcut 'Assign Goal'."*
 *
 * EVERY ONE OF THESE POINTS AT A FLOW THAT ALREADY EXISTS. Nothing here
 * builds a second way to do any of the four things, and the table says
 * which existing flow each lands in:
 *
 *   - **goal** — the "Assign a development goal" dialog this page already
 *     owns (`cp-goal-dialog`, opened by `openGoalDialog`). Unchanged; it
 *     keeps the `cp-assign-goal` testid the e2e dialog config already
 *     declares for `/coach-portal|goals`.
 *   - **training** — `/learn?tab=training&player=<id>&new=1`. That is
 *     `TrainingSessions.jsx`, the ONLY session-scheduling surface in the
 *     app and the one a coach already reaches from the Dashboard's activity
 *     chooser (`activityCreate.js` → "training"). `?new=1` is #79's create
 *     contract, honoured by `useCreateDialog`; `?player=<id>` is the param
 *     `TrainingSessions` already reads to pick which linked player a
 *     scheduled session is for. Both are existing contracts — this adds a
 *     caller, not a mechanism.
 *   - **feedback** — this page's Feedback tab, whose composer
 *     (`feedback-input` / `feedback-add`) is the coach's feedback flow.
 *   - **guide** — this page's Guides tab, whose picker (`cp-guide-select`,
 *     #382) is the assign-a-guide flow.
 *
 * The table lives here, not in the ribbon component, for
 * `activityCreate.js`'s reason: this repo mounts no components in unit
 * tests (#332), so a destination written inline in JSX can only be checked
 * by the e2e suite. `coachShortcuts.test.js` asserts every `tab` target is
 * a tab CoachPortal actually renders and that the training link lands on
 * the page that owns the session dialog — i.e. that no button here opens
 * nothing.
 */

/**
 * @param {string|null} playerId the selected player, or null before the
 *   roster resolves. The training link needs one; without it the option is
 *   omitted rather than pointing at a bare `/learn`, which would schedule
 *   for whichever player the coach's roster happens to list first.
 */
export function coachPortalShortcuts(playerId) {
  const items = [
    {
      key: "goal",
      label: "Assign goal",
      testId: "cp-assign-goal",
      action: { type: "dialog", dialog: "goal" },
    },
    {
      key: "feedback",
      label: "Provide feedback",
      testId: "cp-shortcut-feedback",
      // `focus` asks the panel to put the cursor in its composer — the
      // difference between "here is the tab" and "here is the flow".
      action: { type: "tab", tab: "feedback", focus: true },
    },
    {
      key: "guide",
      label: "Assign coaching guide",
      testId: "cp-shortcut-guide",
      action: { type: "tab", tab: "guides" },
    },
  ];

  if (playerId) {
    items.splice(1, 0, {
      key: "training",
      label: "Schedule training session",
      testId: "cp-shortcut-training",
      action: {
        type: "link",
        to: withCreate(`/learn?tab=training&player=${encodeURIComponent(playerId)}`),
      },
    });
  }

  return items;
}
